import { Schema } from "effect";
import {
  CustomerInfoSchema,
  type CustomerInfo,
} from "../value-objects/CustomerInfo";
import { OrderItemSchema, type OrderItem } from "../value-objects/OrderItem";
import { MoneySchema, type Money } from "../value-objects/Money";
import { CurrencySchema } from "../value-objects/Currency";
import {
  calculateGrandTotal,
  validateGrandTotalCalculation,
} from "../value-objects/InvoiceFinancials";

/**
 * Schema for Order entity with complete validation
 */
export const OrderSchema = Schema.Struct({
  orderId: Schema.String.pipe(Schema.nonEmptyString()),
  createdOn: Schema.DateFromSelf,
  shippingCost: MoneySchema,
  items: Schema.Array(OrderItemSchema).pipe(
    Schema.minItems(1, {
      message: () => "Order must contain at least one item",
    })
  ),
  customer: CustomerInfoSchema,
  subtotal: MoneySchema,
  discount: MoneySchema,
  taxes: MoneySchema,
  grandTotal: MoneySchema,
}).pipe(
  Schema.filter((order) => validateGrandTotalCalculation(order), {
    message: () =>
      "Grand total must equal subtotal + taxes + shipping cost - discount",
  }),
  Schema.filter(
    (order) => {
      // Validate that subtotal matches sum of all item line prices
      const calculatedSubtotal = order.items.reduce(
        (sum, item) => item.linePrice.amount + sum,
        0
      );
      const tolerance = 0.01; // 1 cent tolerance for floating point precision
      return (
        Math.abs(order.subtotal.subtractNumeric(calculatedSubtotal).amount) <=
        tolerance
      );
    },
    {
      message: () => "Subtotal must equal the sum of all item line prices",
    }
  )
);

/**
 * Order entity representing a complete order with customer information and items.
 * This entity contains all the data needed to create an invoice.
 */
export type Order = Schema.Schema.Type<typeof OrderSchema>;

/**
 * Factory function to create and validate Order
 */
export const createOrder = (
  data: unknown
): Schema.Schema.Encoded<typeof OrderSchema> =>
  Schema.decodeUnknownSync(OrderSchema)(data);

/**
 * Validation function that returns an Effect
 */
export const validateOrder = (data: unknown) =>
  Schema.decodeUnknown(OrderSchema)(data);

/**
 * Helper function to calculate subtotal from order items
 */
export const calculateSubtotal = (items: OrderItem[]): Money => {
  const subtotal = items.reduce(
    (sum, item) => item.linePrice.add(sum),
    new MoneySchema({ currency: CurrencySchema.euro, amount: 0 })
  );
  return subtotal.roundTwoDecimals();
};

/**
 * Factory function to create Order with automatic calculations.
 * 
 * The following values are automatically calculated:
 * - subtotal
 * - grand total
 */
export const createOrderWithCalculations = (data: {
  orderId: string;
  createdOn: Date;
  shippingCost: Money;
  items: OrderItem[];
  customer: CustomerInfo;
  taxes: Money;
  discount: Money;
}): Schema.Schema.Encoded<typeof OrderSchema> => {
  const subtotal = calculateSubtotal(data.items);
  const grandTotal = calculateGrandTotal(
    subtotal,
    data.taxes,
    data.shippingCost,
    data.discount
  );

  return createOrder({
    ...data,
    subtotal,
    grandTotal,
  });
};
